/**
 * One-shot "add this to your bet slip" prompt for prop-based Daily Reveal.
 *
 * The player picks a prop on the reveal screen and lands back on the main Daily
 * Reveal page; the first time that happens for a given reveal day we surface the
 * Sportsbook handoff as a modal, then never again for that day. The inline CTA on
 * the pick card stays put as the permanent way back to it, so dismissing the modal
 * costs the player nothing.
 *
 * Keyed on the reveal day rather than the entry id on purpose. The entry id arrives
 * only once the entry query refetches, but the prompt has to fire on the optimistic
 * render immediately after the reveal — keying on the entry would either delay the
 * prompt past the moment it is about to describe, or show it twice as the key
 * changed from null to real. A Daily Reveal entry is one-per-player-per-day, so the
 * day id identifies the same thing and is stable from the first render.
 */

const STORAGE_PREFIX = 'daily-reveal:betslip-prompt-seen';

export function buildBetslipPromptSeenKey(dayId: string): string {
  return `${STORAGE_PREFIX}:${dayId}`;
}

export function hasSeenBetslipPrompt(key: string): boolean {
  try {
    return localStorage.getItem(key) != null;
  } catch {
    // Private mode — the hook's in-memory guard still stops a re-open loop within
    // the session; the cost of the storage failure is one extra prompt next visit.
    return false;
  }
}

export function markBetslipPromptSeen(key: string): void {
  try {
    localStorage.setItem(key, '1');
  } catch {
    // See above — non-fatal.
  }
}

export type ResolveBetslipPromptInput = {
  /**
   * Whether the surrounding page is in a state where a betslip prompt makes sense:
   * a live prop contest the player has picked in, not a history view, not past cutoff.
   */
  enabled: boolean;
  dayId: string | null | undefined;
  /** Result of `buildSportsbookBetslipDeeplink` — null means there is nothing to open. */
  deeplink: string | null | undefined;
  alreadySeen: boolean;
};

/**
 * Whether to auto-open the prompt.
 *
 * Deliberately conservative: no deeplink means no prompt at all, so an unsupported
 * sport or a pick whose prop never froze a sportsbook selection id shows nothing
 * rather than an empty modal.
 */
export function shouldOpenBetslipPrompt(input: ResolveBetslipPromptInput): boolean {
  if (!input.enabled) return false;
  if (!input.dayId) return false;
  if (!input.deeplink) return false;
  return !input.alreadySeen;
}
